import { useRef, useCallback } from 'preact/hooks';
import { SOFT_CAP_MS, LEVEL_BAR_COUNT } from '../hooks/useRecorder';
import { haptic } from '../utils/haptic';
import { MicIcon, StopIcon, CancelIcon } from './ComposerIcons';
import type { VoiceState } from '../utils/voiceState';
import { voiceStateLabel } from '../utils/voiceState';

// Ring drains to 100% at soft cap (3 min) to create urgency before the hard cap.
const RING_DRAIN_OVER_MS = SOFT_CAP_MS;
const LOCK_PRESS_MS = 500;
const LOCK_DRIFT_PX = 10;

function formatDuration(ms: number): string {
  const totalSec = Math.floor(ms / 1000);
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

interface Props {
  busy: boolean;
  onCancel?: () => void;
  isRecording: boolean;
  isLocked: boolean;
  durationMs: number;
  levelBars: number[];
  atSoftCap: boolean;
  transcribing: boolean;
  onMicToggle: (e: Event) => void;
  onLock: () => void;
  onCancelRecording: () => void;
  onExtendCap: () => void;
  micError?: string | null;
  /** Another session currently holds the mic (composerStore.globalRecording
   *  pins to a different sessionId). Render a non-startable "in use elsewhere"
   *  variant — the textarea stays usable, only the mic is gated. Clears
   *  automatically when that session's recording ends (AUDIO_END releases the
   *  global slot). See plans/composer-pipeline-busy-render.md. */
  heldElsewhere?: boolean;
  /** Desktop composer voice chrome (lane va-ui-composer-fsm-2026-10-01, issue #2204):
   *  the machine state the mic renders — DesktopShell derives it (utils/voiceState.ts,
   *  the same derivation the home hero consumes) and threads it in. Pure renderer: the
   *  recorder/guards stay in the shared InputBar + composerStore. When present, the
   *  cluster carries data-voice-state + the state ring/bars + the visible state label,
   *  and the control icons render real SVGs instead of emoji glyphs. Undefined on
   *  mobile → the shared DOM there is byte-identical to pre-lane. */
  voiceState?: VoiceState;
}

export function PTTButton({
  busy,
  onCancel,
  isRecording,
  isLocked,
  durationMs,
  levelBars,
  atSoftCap,
  transcribing,
  onMicToggle,
  onLock,
  onCancelRecording,
  onExtendCap,
  micError,
  heldElsewhere,
  voiceState,
}: Props) {
  const longPressTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pressStartRef = useRef<{ x: number; y: number } | null>(null);
  // The desktop voice-chrome class + state marker — applied to EVERY branch root so the
  // cluster contract (.desktop-composer-mic[data-voice-state]) is stable across the
  // busy/dock/held/idle phases; empty when the chrome is not armed (mobile, byte-identical).
  const chromeCls = voiceState ? ' desktop-composer-mic' : '';
  const chromeAttr = { 'data-voice-state': voiceState };

  // touchstart/touchend with drift guard for long-press-to-lock.
  // Touch events (not pointerdown) avoid conflict with page scroll.
  const handleTouchStart = useCallback((e: TouchEvent) => {
    if (!isRecording || isLocked) return;
    const t = e.touches[0];
    pressStartRef.current = { x: t.clientX, y: t.clientY };
    longPressTimer.current = setTimeout(() => {
      longPressTimer.current = null;
      haptic(30);
      onLock();
    }, LOCK_PRESS_MS);
  }, [isRecording, isLocked, onLock]);

  const handleTouchEnd = useCallback(() => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  }, []);

  const handleTouchMove = useCallback((e: TouchEvent) => {
    if (!pressStartRef.current || !longPressTimer.current) return;
    const t = e.touches[0];
    const dx = Math.abs(t.clientX - pressStartRef.current.x);
    const dy = Math.abs(t.clientY - pressStartRef.current.y);
    if (dx > LOCK_DRIFT_PX || dy > LOCK_DRIFT_PX) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  }, []);

  if (busy) {
    return (
      <div class={`ptt-area${chromeCls}`} {...chromeAttr}>
        <div class="ptt-row">
          <button class="ptt-button cancel" onClick={onCancel} title="Cancel running prompt">
            {voiceState ? <CancelIcon /> : '✖'}
          </button>
        </div>
        <span class="ptt-hint">Tap to cancel</span>
      </div>
    );
  }

  if (isRecording || transcribing) {
    const ringPct = Math.min((durationMs / RING_DRAIN_OVER_MS) * 100, 100);
    const isAmber = durationMs >= 30_000;
    const isRed = durationMs >= 50_000;
    const ringColor = isRed
      ? 'var(--accent-red, #d32f2f)'
      : isAmber
        ? '#f59e0b'
        : 'var(--theme-accent, #7c6af7)';

    return (
      <div class={`ptt-area${chromeCls}`} {...chromeAttr}>
        {/* extend chip hidden — hard cap is 60s, extending is no longer offered */}
        <div
          class={`ptt-dock${isLocked ? ' ptt-dock--locked' : ''}${transcribing ? ' ptt-dock--transcribing' : ''}`}
          style={{
            '--ring-pct': `${ringPct}%`,
            '--ring-color': ringColor,
          } as Record<string, string>}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
          onTouchMove={handleTouchMove}
        >
          <div class="ptt-bars" aria-hidden="true">
            {(transcribing ? Array(LEVEL_BAR_COUNT).fill(0) : levelBars).map((v, i) => (
              <span
                key={i}
                class="ptt-bar"
                style={{ transform: `scaleY(${Math.max(0.08, v / 100)})` }}
              />
            ))}
          </div>

          <span class="ptt-duration">
            {transcribing ? 'Transcribing…' : formatDuration(durationMs)}
          </span>

          {!transcribing && (
            <button
              class="ptt-dock-stop"
              onClick={onMicToggle}
              title="Stop and send"
              aria-label="Stop and send"
            >
              {voiceState ? <StopIcon /> : '⏹'}
            </button>
          )}

          <button
            class="ptt-dock-cancel"
            onClick={(e) => { e.stopPropagation(); onCancelRecording(); }}
            title="Cancel"
            aria-label="Cancel recording"
          >
            {voiceState ? <CancelIcon /> : '✕'}
          </button>
        </div>
        <span class="ptt-hint">
          {transcribing
            ? `Transcribing ${formatDuration(durationMs)}…`
            : isLocked
              ? 'Locked · tap ⏹ to send'
              : 'Hold 0.5s to lock · tap ⏹ to send'}
        </span>
      </div>
    );
  }

  // Another session holds the mic — render a non-startable variant. The tap
  // still routes to onMicToggle so InputBar can surface a brief hint, but the
  // guard there refuses to start a second recording. This branch only renders
  // when THIS session is idle (the isRecording/transcribing/busy branches
  // above return first), so it never masks an active local recording.
  if (heldElsewhere) {
    return (
      <div class={`ptt-area${chromeCls}`} {...chromeAttr}>
        <div class="ptt-row">
          <button
            class="ptt-button whisper-mic whisper-mic--held"
            onClick={onMicToggle}
            title="Microphone is recording in another session"
            aria-label="Microphone in use in another session"
            aria-disabled="true"
          >
            {voiceState ? <MicIcon /> : '🎙'}
          </button>
        </div>
        {voiceState ? (
          <span class="desktop-composer-mic-label" role="status" aria-live="polite">
            {voiceStateLabel(voiceState)}
          </span>
        ) : (
          <span class="ptt-hint ptt-hint--held">In use · another session</span>
        )}
      </div>
    );
  }

  return (
    <div class={`ptt-area${chromeCls}`} {...chromeAttr}>
      <div class="ptt-row">
        <button
          class="ptt-button whisper-mic"
          data-coach-id="mic"
          onClick={onMicToggle}
          title="Tap to record (Whisper)"
          aria-label="Start recording"
        >
          {voiceState ? (
            <>
              <MicIcon />
              <span class="desktop-composer-mic-ring" aria-hidden="true" />
              <span class="desktop-composer-mic-bars" aria-hidden="true">
                <span class="desktop-composer-mic-bar" />
                <span class="desktop-composer-mic-bar" />
                <span class="desktop-composer-mic-bar" />
              </span>
            </>
          ) : (
            '🎙'
          )}
        </button>
      </div>
      {micError ? (
        <span class="ptt-hint ptt-hint--error">{micError}</span>
      ) : voiceState ? (
        // The visible state label doubles as the control's label — the same contract
        // as the hero's (Ready / Listening… / Thinking… / Speaking…).
        <span class="desktop-composer-mic-label" role="status" aria-live="polite">
          {voiceStateLabel(voiceState)}
        </span>
      ) : (
        <span class="ptt-hint">Tap to record</span>
      )}
    </div>
  );
}
